import React, { useRef, useLayoutEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

export default function ScienceIntake() {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const root = ref.current;
      if (!root) return;

      gsap.from(root.querySelectorAll(".si-head > *"), {
        y: 18, autoAlpha: 0, duration: 0.55, ease: "power3.out", stagger: 0.08,
        scrollTrigger: { trigger: root, start: "top 85%", once: true }
      });

      gsap.from(root.querySelectorAll(".si-step"), {
        y: 24, autoAlpha: 0, duration: 0.55, ease: "power3.out", stagger: 0.08,
        scrollTrigger: { trigger: ".si-grid", start: "top 82%", once: true }
      });

      root.querySelectorAll(".si-ill").forEach((img) => {
        gsap.to(img, {
          yPercent: -6, ease: "none",
          scrollTrigger: { trigger: img.closest(".si-step"), start: "top bottom", end: "bottom top", scrub: true }
        });
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section id="science-intake" className="science-intake" ref={ref} aria-labelledby="si-title">
      <div className="si-inner">
        <header className="si-head">
          <p className="si-kicker">The importance of intake outside events</p>
          <h2 id="si-title" className="si-title">Fuel habits that make the hard days easier.</h2>
          <p className="si-lede">
            Carbohydrate practice isn’t just for race day—your body (and gut) adapt over time.
          </p>
        </header>

        <div className="si-grid">
          <article className="si-step">
            <div className="si-media"><img className="si-ill" src="/DSCF9477.jpg" alt="Daily fueling routine" loading="lazy" /></div>
            <div className="si-body">
              <span className="si-num">01</span>
              <h3>Don’t run on empty</h3>
              <p>
                Train the way you intend to perform—<strong>topped up</strong>. Using Dope Shot daily helps your gut
                familiarize with the product, making ingested carbohydrates (Dope Shot) easier to use for energy
                (<em>exogenous carbohydrate oxidation</em>).
              </p>
            </div>
          </article>

          <article className="si-step">
            <div className="si-media"><img className="si-ill" src="/DSCF9477.jpg" alt="Pre-workout sip" loading="lazy" /></div>
            <div className="si-body">
              <span className="si-num">02</span>
              <h3>Right before you start</h3>
              <p>
                Take a couple shots (<strong>100–200 calories</strong>) prior to exercise. It can lift performance and
                stays easy on the stomach—especially when nerves are high.
              </p>
            </div>
          </article>

          <article className="si-step">
            <div className="si-media"><img className="si-ill" src="/DSCF9477.jpg" alt="Post-workout refuel" loading="lazy" /></div>
            <div className="si-body">
              <span className="si-num">03</span>
              <h3>Post-workout window</h3>
              <p>
                Refuel immediately after your session. During the <strong>30–60 minute glycogen window</strong> your
                body absorbs fuel more readily—helping you be ready for tomorrow. Mix it up if you like (juice, soda,
                Gatorade). Dope Shot isn’t the only way; it’s a <strong>tool in your nutrition toolbox</strong>.
              </p>
            </div>
          </article>

          <article className="si-step">
            <div className="si-media"><img className="si-ill" src="/DSCF9477.jpg" alt="Gut training concept" loading="lazy" /></div>
            <div className="si-body">
              <span className="si-num">04</span>
              <h3>Gut training matters</h3>
              <p>
                A product designed for high carbohydrate intake (like Dope Shot) <strong>necessitates and facilitates</strong>
                gut training. Efficacy depends on consistent integration into your training program—just like the pros
                who prioritize this adaptation for peak performance.
              </p>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
